import { DEFAULT_LOCALE, type Locale } from "@tami/core/intl";

/**
 * Prefixo de idioma das páginas públicas indexáveis (home, `/demo`, `/links`,
 * termos de uso e política de privacidade).
 * O português fica sem prefixo — `/` é o endereço que já está no Google — e
 * o inglês e o espanhol ganham `/en` e `/es`. Por dentro, o português também
 * é um segmento (`/pt`), para as três versões saírem do mesmo
 * `app/[lang]`; o proxy reescreve `/` para `/pt` sem mudar a URL.
 */
export const LOCALE_SEGMENTS = {
  "pt-BR": "pt",
  "en-US": "en",
  "es-ES": "es",
} as const satisfies Record<Locale, string>;

export type LocaleSegment = (typeof LOCALE_SEGMENTS)[Locale];

export const PUBLIC_LOCALE_SEGMENTS: readonly LocaleSegment[] = Object.values(LOCALE_SEGMENTS);

/** Segmento interno do português: nunca aparece na URL pública. */
export const DEFAULT_LOCALE_SEGMENT: LocaleSegment = LOCALE_SEGMENTS["pt-BR"];

/**
 * Termos de uso e política de privacidade: existem nos três idiomas, mas a
 * versão em português prevalece. Por isso o endereço sem prefixo é sempre o
 * português, para qualquer visitante — a nota de cada tradução aponta para
 * ele, e um redirecionamento por preferência o tornaria inalcançável.
 */
export const LEGAL_PUBLIC_PATHS = ["/termos-de-uso", "/politica-de-privacidade"] as const;

/** As páginas que existem nos três idiomas, sem prefixo. */
export const LOCALIZED_PUBLIC_PATHS = ["/", "/demo", "/links", ...LEGAL_PUBLIC_PATHS] as const;

export function isLegalPublicPath(path: string): boolean {
  return (LEGAL_PUBLIC_PATHS as readonly string[]).includes(path);
}

export function localeFromSegment(segment: string | undefined): Locale | null {
  const entry = Object.entries(LOCALE_SEGMENTS).find(([, value]) => value === segment);
  return entry ? (entry[0] as Locale) : null;
}

/** "/" → "/" em pt, "/en" em inglês; "/demo" → "/es/demo" em espanhol. */
export function localizedPath(path: string, locale: Locale): string {
  if (locale === DEFAULT_LOCALE) return path;
  const prefix = `/${LOCALE_SEGMENTS[locale]}`;
  return path === "/" ? prefix : `${prefix}${path}`;
}

/**
 * Lê o idioma do prefixo de uma URL pública: "/en/demo" → en-US e "/demo".
 * Sem prefixo é português. Devolve `null` para caminho que não é uma página
 * pública traduzida (o proxy não mexe nele).
 */
export function parseLocalizedPublicPath(
  pathname: string,
): { readonly locale: Locale; readonly path: string; readonly prefixed: boolean } | null {
  const match = /^\/(pt|en|es)(\/.*)?$/.exec(pathname);
  const locale = match ? localeFromSegment(match[1]) : DEFAULT_LOCALE;
  const rest = match ? (match[2] ?? "/") : pathname;
  const path = rest.length > 1 ? rest.replace(/\/+$/, "") : rest;
  if (!locale || !(LOCALIZED_PUBLIC_PATHS as readonly string[]).includes(path)) return null;
  return { locale, path, prefixed: Boolean(match) };
}
